/**
 * Utility functions for card comparison calculations used in peer discovery and procedure comparison graphs
 */

/**
 * Get all unique categories from cards and map them to colors
 * @param {Array} cards - Array of card objects with usage_data and item_cost_data
 * @param {Array} colors - Array of color values to map to categories
 * @returns {Object} { uniqueCategories: Array, categoryColorMap: Object }
 */
export function getCategoriesAndColors(cards, colors) {
    const allCategories = cards.flatMap(card => 
        Object.keys(card.usage_data?.categories || {})
            .concat(Object.keys(card.item_cost_data?.categories || {}))
    );
    const uniqueCategories = Array.from(new Set(allCategories)).sort();
    
    const categoryColorMap = {};
    uniqueCategories.forEach((cat, idx) => {
        categoryColorMap[cat] = colors[idx % colors.length];
    });
    
    return { uniqueCategories, categoryColorMap };
}

/**
 * Prepare cards with filtered and sorted entries based on selected toggle and filter categories
 * This adds an 'entries' property to each card, and sorts the entries by category name
 * @param {Array} cards - Array of card objects
 * @param {string} dataKey - 'usage_data' or 'item_cost_data'
 * @param {Array} filterCategories - Array of selected filter categories
 * @returns {Array} Prepared cards with entries property
 */
export function prepareCardsWithEntries(cards, dataKey, filterCategories = []) {
    return cards.map(card => ({
        ...card,
        entries: Object.entries(card[dataKey]?.categories || {})
            .filter(([cat]) => filterCategories.length === 0 || filterCategories.includes(cat))
            .sort((a, b) => a[0].localeCompare(b[0]))
    }));
}

/**
 * Sort cards themselves by total item cost (lowest first)
 * @param {Array} cards - Array of prepared card objects
 * @returns {Array} Sorted cards
 */
export function sortCardsByItemCost(cards) {
    return cards.sort((a, b) => {
        const sumCategories = (card) => {
            const cats = card?.item_cost_data?.categories ?? {};
            return Object.values(cats).reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0);
        };

        const totalA = sumCategories(a);
        const totalB = sumCategories(b);
        return totalA - totalB; // Lowest card cost first
    });
}

/**
 * Calculate maximum total for filtered categories across both usage and item cost data
 * @param {Array} cards - Array of card objects
 * @param {Array} filterCategories - Array of selected filter categories
 * @returns {Object} { maxUsageTotal: number, maxItemCostTotal: number }
 */
export function calculateMaxTotalsForFilteredCategories(cards, filterCategories = []) {
    const maxUsageTotal = cards.reduce((max, card) => {
        const total = Object.entries(card.usage_data?.categories || {})
            .filter(([cat]) => filterCategories.length === 0 || filterCategories.includes(cat))
            .reduce((sum, [, val]) => sum + val, 0);
        return Math.max(max, total);
    }, 0);
    
    const maxItemCostTotal = cards.reduce((max, card) => {
        const total = Object.entries(card.item_cost_data?.categories || {})
            .filter(([cat]) => filterCategories.length === 0 || filterCategories.includes(cat))
            .reduce((sum, [, val]) => sum + val, 0);
        return Math.max(max, total);
    }, 0);
    
    return { maxUsageTotal, maxItemCostTotal };
}

/**
 * Generate filtered categories display text
 * @param {Array} filterCategories - Array of selected filter categories
 * @param {Array} uniqueCategories - Array of all available categories
 * @returns {string} Display text for filtered categories
 */
export function getFilteredCategoriesText(filterCategories, uniqueCategories) {
    if (filterCategories.length === 0 || filterCategories.length === uniqueCategories.length) {
        return 'All Categories';
    } else if (filterCategories.length === 1) {
        return filterCategories[0];
    } else {
        return `Showing ${filterCategories.length} of ${uniqueCategories.length} Categories`;
    }
}